/**
 * ForwardToFriend — public "you've been invited to join U.S. LawShield" page (/refer/:token).
 *
 * Reached by a friend with NO auth session (the referral link in a member's voucher/reward email).
 * Full-bleed, mobile-first, no app sidebar. Resolves the token, captures the friend (creates a pending
 * digital catalyst server-side), then hands off to USLS's existing join flow. Attribution rides the
 * friend's email + the Zuora membership-confirm trigger — so the page makes the "use the same email"
 * dependency explicit.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  resolveReferral,
  captureReferral,
} from "../services/forwardToFriend.service";

const DEFAULT_JOIN_URL = "https://www.uslawshield.com/";

const ForwardToFriend: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  // Tokenless /refer = the verbal path: the friend types the referring member's number.
  const verbalMode = !token;

  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [eventTitle, setEventTitle] = useState<string | null>(null);
  const [joinUrl, setJoinUrl] = useState<string>(DEFAULT_JOIN_URL);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [memberNo, setMemberNo] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [doneEmail, setDoneEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        // Verbal path: no token to resolve — show the form (inviter is resolved at submit
        // time from the member number the friend enters).
        setLoading(false);
        setValid(true);
        return;
      }
      try {
        const res = await resolveReferral(token);
        if (cancelled) {
          return;
        }
        setValid(!!res.valid);
        setEventTitle(res.eventTitle ?? null);
        if (res.joinUrl) {
          setJoinUrl(res.joinUrl);
        }
      } catch {
        if (!cancelled) {
          setValid(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (verbalMode && !memberNo.trim()) {
        setError("Please enter the member number that referred you.");
        return;
      }
      setError(null);
      setSubmitting(true);
      try {
        const res = await captureReferral({
          ...(token ? { token } : {}),
          ...(verbalMode ? { referredByMemberNo: memberNo.trim() } : {}),
          email: email.trim().toLowerCase(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        });
        if (res.ok) {
          setDoneEmail(res.email || email.trim().toLowerCase());
          if (res.joinUrl) {
            setJoinUrl(res.joinUrl);
          }
          setDone(true);
        } else {
          switch (res.error) {
            case "self_referral":
              setError(
                "This is your own invite link — share it with a friend instead.",
              );
              break;
            case "invalid_email":
              setError("Please enter a valid email address.");
              break;
            case "invalid_token":
              setError("This invite link is invalid or has expired.");
              break;
            case "member_not_found":
              setError(
                "We couldn't match that member number. Double-check it with your friend, or ask them for their referral link.",
              );
              break;
            case "limit_reached":
              setError(
                "This invite has reached its limit. Please check back later.",
              );
              break;
            default:
              setError("Something went wrong. Please try again.");
          }
        }
      } catch {
        setError("We couldn't reach the server. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
    [token, verbalMode, memberNo, email, firstName, lastName],
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Brand header */}
      <header className="bg-[#C8102E] py-5 px-4 text-center">
        <img
          src="https://uslscrm.trysynapse.ai/USLSlogonobackground.png"
          alt="U.S. LawShield"
          className="mx-auto h-10 w-auto"
        />
      </header>

      <main className="flex-1 w-full max-w-md mx-auto px-4 py-8">
        {loading ? (
          <p className="text-center text-gray-500">Loading…</p>
        ) : !valid ? (
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center">
            <h1 className="text-lg font-semibold text-gray-900 mb-2">
              Invite link not found
            </h1>
            <p className="text-sm text-gray-600">
              This invite link is invalid or has expired. Please ask your friend
              for a new link.
            </p>
          </div>
        ) : done ? (
          <div className="rounded-lg border border-green-200 bg-white p-6 text-center">
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              You're all set! 🎉
            </h1>
            <p className="text-sm text-gray-700 mb-4">
              The last step is to finish joining U.S. LawShield.
            </p>
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 mb-5">
              Important: join using <strong>{doneEmail}</strong> — the same
              email you entered here — so your friend's referral credit applies
              automatically.
            </div>
            <a
              href={joinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full rounded-md bg-[#C8102E] px-4 py-3 text-center text-sm font-semibold text-white hover:bg-[#a50d25]"
            >
              Join U.S. LawShield
            </a>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h1 className="text-xl font-bold text-gray-900 mb-1 text-center">
              You've been invited to join U.S. LawShield
            </h1>
            {eventTitle ? (
              <p className="text-sm text-gray-600 mb-4 text-center">
                via {eventTitle}
              </p>
            ) : (
              <div className="mb-4" />
            )}

            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 mb-5">
              Use <strong>this same email</strong> when you join U.S. LawShield
              — your friend's referral credit only applies automatically if you
              join with the same email.
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              {verbalMode && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Referring member number{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={memberNo}
                    onChange={(e) => setMemberNo(e.target.value)}
                    placeholder="The member # your friend gave you"
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    First name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Last name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              {error && (
                <p className="text-sm text-red-600" role="alert">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={
                  submitting ||
                  !email.trim() ||
                  !firstName.trim() ||
                  (verbalMode && !memberNo.trim())
                }
                className="w-full rounded-md bg-[#C8102E] px-4 py-3 text-sm font-semibold text-white hover:bg-[#a50d25] disabled:opacity-50"
              >
                {submitting ? "Submitting…" : "Continue"}
              </button>
            </form>
          </div>
        )}
      </main>

      <footer className="py-6 text-center text-xs text-gray-400">
        &copy; {new Date().getFullYear()} U.S. LawShield
      </footer>
    </div>
  );
};

export default ForwardToFriend;
